import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LOGO } from '../../../assets';
import TextField from '../../../components/TextField';
import Button from '../../../components/Button';
import { FiLock, FiMail } from 'react-icons/fi';
import Icon from '../../../components/Icon';
import { signInWithEmail, findBankAndPsoByAuthUidWithFallback, findBankByAuthUid, signOutUser } from '../service/auth';
import { useAppDispatch } from '../../../store';
import { setUser, setBank, setPso } from '../../../store/slices';
import Toast from '../../../components/Toast';

export default function Login() {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        try {
            const user = await signInWithEmail(email, password);
            // Dynamically find PSO across all banks by auth UID, with fallback strategies
            const userRecord = {
                uid: user.uid,
                email: user.email,
                displayName: user.displayName ?? null,
                photoURL: user.photoURL ?? null,
            } as const;

            try {
                const { bank, pso } = await findBankAndPsoByAuthUidWithFallback(user.uid);
                dispatch(setUser(userRecord));
                dispatch(setBank({
                    userId: bank.userId,
                    companyId: bank.companyId,
                    companyName: bank.companyName,
                    companyNo: bank.companyNo,
                    companyAddress: bank.companyAddress,
                    email: bank.email,
                    imgUrl: bank.imgUrl,
                    createdAt: bank.createdAt,
                }));
                dispatch(setPso({
                    authUid: pso.authUid,
                    name: pso.name,
                    email: pso.email,
                    contact: pso.contact,
                    designation: pso.designation,
                    createdAt: pso.createdAt,
                }));
                sessionStorage.removeItem('toast');
                // Set role based on designation - incharge users are still stored as 'pso' role
                // but the designation field identifies them as incharge
                const isIncharge = pso.designation && /incharge/i.test(pso.designation);
                sessionStorage.setItem('role', isIncharge ? 'incharge' : 'pso');
                sessionStorage.removeItem('returnPath'); // Clear any saved return path
                navigate('/dashboard', { replace: true });
            } catch (e: any) {
                // If PSO not found, attempt bank login
                const bankResult = await findBankByAuthUid(user.uid);
                if (bankResult) {
                    const { bank } = bankResult;
                    dispatch(setUser(userRecord));
                    dispatch(setBank({
                        userId: bank.userId,
                        companyId: bank.companyId,
                        companyName: bank.companyName,
                        companyNo: bank.companyNo,
                        companyAddress: bank.companyAddress,
                        email: bank.email,
                        imgUrl: bank.imgUrl,
                        createdAt: bank.createdAt,
                    }));
                    // Ensure PSO is cleared for bank users
                    dispatch(setPso(null));
                    sessionStorage.removeItem('toast');
                    sessionStorage.setItem('role', 'bank');
                    sessionStorage.removeItem('returnPath'); // Clear any saved return path
                    navigate('/dashboard', { replace: true });
                } else {
                    await signOutUser();
                    setError('This account is not found as PSO.');
                    return; // stay on login
                }
            }
        } catch (err: any) {
            setError(err?.message || 'Login failed');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-indigo-400 to-purple-600 dark:from-[#1a1a2e] dark:via-[#16213e] dark:to-[#0f3460]">
            {/* Global toast position (top-right), consistent with dashboard */}
            <div className="pointer-events-none fixed right-4 top-4 z-50 space-y-2">
                {error && (
                    <div className="pointer-events-auto">
                        <Toast message={error} type="error" onClose={() => setError(null)} />
                    </div>
                )}
            </div>
            {/* animated grid overlay like your reference */}
            <div className="pointer-events-none absolute inset-0 grid-bg animate-[grid-move_20s_linear_infinite]" />
            {/* subtle corner glow gradients */}
            <div className="pointer-events-none absolute -left-32 -top-32 h-80 w-80 rounded-full bg-gradient-to-br from-white/20 to-transparent blur-3xl dark:from-blue-500/20" />
            <div className="pointer-events-none absolute -bottom-32 -right-32 h-80 w-80 rounded-full bg-gradient-to-tr from-white/15 to-transparent blur-3xl dark:from-indigo-500/25" />

            <div className="relative z-10 flex min-h-screen items-center justify-center p-4 sm:p-6">
                <div className="grid w-full max-w-6xl grid-cols-1 items-center gap-6 lg:grid-cols-2">
                    {/* Left: centered logo + title */}
                    <div className="flex items-center justify-center">
                        <div className="flex w-full flex-col items-center">
                            <img src={LOGO} alt="Logo" className="h-auto w-28 sm:w-32 drop-shadow-[0_8px_24px_rgba(0,0,0,0.35)]" />
                            <h2 className="mt-6 text-center font-bold tracking-wide text-white text-xl sm:text-2xl">PSO Management System</h2>
                        </div>
                    </div>

                    {/* Right: card form */}
                    <div className="flex items-center justify-center">
                        <div className="w-full max-w-[420px] rounded-2xl border border-gray-200/70 bg-white/80 p-6 shadow-xl backdrop-blur-md dark:border-gray-700/60 dark:bg-black/30 sm:p-8">
                            <div className="mb-6 text-center sm:mb-8">
                                <h1 className="mb-2 text-2xl font-bold text-gray-800 dark:text-gray-100">Welcome Back</h1>
                                <p className="text-sm text-gray-500 dark:text-gray-400">Sign in to your CCTV Portal account</p>
                            </div>

                            <form onSubmit={onSubmit} className="flex flex-col gap-4 sm:gap-6">

                                <div className="flex flex-col gap-2">
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Email Address</label>
                                    <TextField
                                        type="email"
                                        placeholder="Enter your email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                        prefix={<Icon icon={FiMail} className="text-gray-400" />}
                                    />
                                </div>

                                <div className="flex flex-col gap-2">
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Password</label>
                                    <TextField
                                        type="password"
                                        placeholder="Enter your password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                        prefix={<Icon icon={FiLock} className="text-gray-400" />}
                                    />
                                </div>

                                <Button
                                    type="submit"
                                    className="mt-1 w-full bg-gradient-to-br from-blue-500 to-indigo-600 px-6 py-3 text-sm font-semibold text-white shadow-md transition hover:-translate-y-px hover:from-blue-600 hover:to-indigo-700 hover:shadow-lg disabled:opacity-60"
                                    disabled={loading}
                                >
                                    {loading ? 'Signing In...' : 'Sign In'}
                                </Button>
                            </form>
                        </div>
                    </div>
                </div>
            </div>

            <style>{`@keyframes grid-move{0%{transform:translate(0,0)}100%{transform:translate(50px,50px)}}`}</style>
        </div>
    );
}


